import React, { useRef, useEffect, useState, useContext } from 'react'
import { MediaPlayerContext } from 'contexts/PlayerContext'
import classNames from 'classnames'
import useOnScreen from 'hooks/useOnScreen'

const WAVEFORM_URL_FALLBACK = 'https://voicefinder.voicearchive.com/waveform'

const loadWaveform = (wavesurfer, src) => {
  if (src) {
    const waveformUrl =
      (typeof window !== 'undefined' &&
        window.env &&
        window.env.RAZZLE_WAVEFORM_URL) ||
      WAVEFORM_URL_FALLBACK

    fetch(`${waveformUrl}?object_url=${encodeURIComponent(src)}`)
      .then((response) => response.json())
      .then((wavePeaks) => {
        wavesurfer.load(src, wavePeaks.data)
      })
      .catch(() => {
        wavesurfer.load(src)
      })
  }
}

const Waveform = ({ src, height = 64, waveColor = '#EAF0F5', className, ...props }) => {
  const { player, seek, history, onWaveSeek } = useContext(MediaPlayerContext)

  const waveformRef = useRef()
  const onScreen = useOnScreen(waveformRef, '200px')

  const [wavesurfer, setWavesurfer] = useState(null)

  let seekInterval = null

  useEffect(async () => {
    if (onScreen && waveformRef.current && !wavesurfer) {
      const WaveSurfer = (await import('wavesurfer.js')).default
      const wavesurferObj = WaveSurfer.create({
        container: waveformRef.current,
        height, // px = 4rem = h-16
        barWidth: 2,
        barHeight: 1,
        barGap: null,
        waveColor: waveColor,
        cursorColor: 'transparent',
        fillParent: true,
        hideScrollbar: true,
      })
      setWavesurfer(wavesurferObj)

      wavesurferObj.on('ready', () => {
        if (wavesurferObj && history.hasOwnProperty(src)) {
          wavesurferObj.seekTo(history[src].percentage)
        }
      })

      loadWaveform(wavesurferObj, src)
    }

    return () => {
      if (seekInterval) clearInterval(seekInterval)
    }
  }, [onScreen])

  useEffect(() => {
    return () => {
      if (wavesurfer) {
        wavesurfer.destroy()
      }
    }
  }, [wavesurfer])

  useEffect(() => {
    if (wavesurfer && src) {
      loadWaveform(wavesurfer, src)
    }
  }, [src])

  useEffect(() => {
    if (player && wavesurfer && src === seek.src) {
      wavesurfer.seekTo(seek.value)
    }
  }, [seek])

  const seekWave = () => {
    if (wavesurfer) {
      wavesurfer.on('seeking', () => {
        onWaveSeek({
          srcUrl: src,
          time: wavesurfer.getCurrentTime(),
          percentage: wavesurfer.getCurrentTime() / wavesurfer.getDuration(),
        })
      }, {
        once: true
    })
    }
  }

  return (
    <div
      ref={waveformRef}
      className={classNames('w-full overflow-hidden z-0', className)}
      onClick={seekWave}
      {...props}
    />
  )
}

export default Waveform
